import { Link } from '@tanstack/react-router';
import {
  ChevronDown,
  CircleDollarSign,
  Database,
  Ellipsis,
  Info,
  User as UserIcon,
} from 'lucide-react';
import {
  BackButton,
  Badge,
  Button,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Skeleton,
  Switch,
  cn,
} from '@wundo/ui';
import type { UserOverview } from '@/types/messages.ts';
import { attentionOf } from '@/components/conversationReview/attentionOf.ts';
import { SEVERITY_MARK_CLASS } from '@/components/conversationReview/severity.ts';
import { formatMessageDateTime } from '@/utils/messageUtils.ts';
import { channelInfo } from './channels.ts';
import type { ConversationGroup } from './conversationGroups.ts';
import { personName } from './personName.ts';

export interface TranscriptHeaderProps {
  person: UserOverview;
  groups: ConversationGroup[];
  selection: ConversationGroup | null;
  /** The conversations are still on their way: the picker is drawn as its shape. */
  loading: boolean;
  groupByChannel: boolean;
  onSelectGroup: (group: ConversationGroup) => void;
  onGroupingChange: (next: boolean) => void;
  showInternal: boolean;
  onShowInternalChange: (next: boolean) => void;
  /** Whether one conversation is being read, which is what the inspector opens on. */
  canInspect: boolean;
  onInspect: () => void;
  onOpenMemories: () => void;
  onOpenCost: () => void;
  /** Back to the people, on a phone, where they are a screen of their own. */
  onBack: () => void;
}

/** Whose transcript this is, which conversation of theirs, and what else there is to open. One line. */
export function TranscriptHeader({
  person,
  groups,
  selection,
  loading,
  groupByChannel,
  onSelectGroup,
  onGroupingChange,
  showInternal,
  onShowInternalChange,
  canInspect,
  onInspect,
  onOpenMemories,
  onOpenCost,
  onBack,
}: TranscriptHeaderProps) {
  return (
    <div data-ui='header' className='flex min-h-14 items-center gap-2 px-4 py-3'>
      {/* Only the chevron shows: the header keeps to one line on a phone. */}
      <BackButton className='hidden max-mobile:inline-flex' onClick={onBack}>
        <span className='sr-only'>All users</span>
      </BackButton>
      <h2 className='min-w-0 flex-1 truncate text-md font-bold text-ink'>
        {personName(person)}
        <span className='font-normal text-ink-muted'> · {person.organisation.name}</span>
      </h2>

      {loading && <Skeleton className='h-8 w-32 shrink-0 rounded-full max-mobile:w-12' />}

      {selection && (
        <Menu>
          <MenuTrigger
            render={
              <Button
                variant='quiet'
                size='sm'
                data-ui='conversation-picker'
                aria-label={`Conversations: ${pickerLabel(selection, groupByChannel)}`}
                className='shrink-0 gap-1.5 px-3 py-1.5 max-mobile:px-2'
              />
            }
          >
            <ChannelIcon channel={selection.channel} />
            <span className='max-mobile:hidden'>
              {channelInfo(selection.channel).label}
              <span className='font-normal text-ink-muted'>
                {' '}
                · {pickerDetail(selection, groupByChannel)}
              </span>
            </span>
            <ChevronDown className='size-3.5' aria-hidden='true' />
          </MenuTrigger>
          <MenuContent align='end' className='max-h-64 w-80 overflow-y-auto'>
            {groups.map((group) => (
              <MenuItem
                key={group.id}
                className={cn(group.id === selection.id && 'bg-fill')}
                onClick={() => onSelectGroup(group)}
              >
                <span className='flex min-w-0 flex-1 items-center gap-2'>
                  <span className='flex shrink-0 items-center gap-1.5'>
                    <ChannelIcon channel={group.channel} />
                    {channelInfo(group.channel).label}
                  </span>
                  <span className='truncate font-normal text-ink-muted'>
                    {group.agent}
                  </span>
                  {group.is_current && <Badge tone='success'>Current</Badge>}
                  <GroupFindingsDot group={group} />
                </span>
                <span className='shrink-0 text-xs font-normal text-ink-muted'>
                  {group.last_message_at
                    ? formatMessageDateTime(group.last_message_at)
                    : 'No messages'}
                </span>
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
      )}

      <Menu>
        <MenuTrigger
          render={
            <IconButton
              size='sm'
              variant='quiet'
              aria-label='Actions'
              data-ui='actions-menu'
            />
          }
        >
          <Ellipsis className='size-4' aria-hidden='true' />
        </MenuTrigger>
        <MenuContent align='end' className='w-64'>
          <MenuItem render={<Link to='/users/$id' params={{ id: person.id }} />}>
            <UserIcon aria-hidden='true' />
            User details
          </MenuItem>

          <MenuSeparator />

          <MenuItem
            /* Menu has no disabled look of its own; this is Button's. */
            className='data-disabled:pointer-events-none data-disabled:opacity-40'
            disabled={!canInspect}
            onClick={onInspect}
          >
            <Info aria-hidden='true' />
            Inspect conversation
          </MenuItem>
          <MenuItem onClick={onOpenMemories}>
            <Database aria-hidden='true' />
            Memories
          </MenuItem>
          <MenuItem onClick={onOpenCost}>
            <CircleDollarSign aria-hidden='true' />
            Cost
          </MenuItem>

          <MenuSeparator />

          {/* A switch is a button, so it is named rather than labelled. */}
          <div className='flex flex-col gap-2.5 px-2.75 py-2.5'>
            <div className='flex items-center justify-between gap-3'>
              <span id='group-by-channel' className='text-sm text-ink-secondary'>
                Group by channel
              </span>
              <Switch
                aria-labelledby='group-by-channel'
                size='sm'
                checked={groupByChannel}
                onCheckedChange={onGroupingChange}
              />
            </div>
            <div className='flex items-center justify-between gap-3'>
              <span id='show-internal' className='text-sm text-ink-secondary'>
                Show internal messages
              </span>
              <Switch
                aria-labelledby='show-internal'
                size='sm'
                checked={showInternal}
                onCheckedChange={onShowInternalChange}
              />
            </div>
          </div>
        </MenuContent>
      </Menu>
    </div>
  );
}

/* "5 conversations" for a channel read as one; the agent for a single conversation. */
function pickerDetail(group: ConversationGroup, grouped: boolean): string {
  if (!grouped) return group.agent;
  const count = group.conversations.length;
  return `${count} ${count === 1 ? 'conversation' : 'conversations'}`;
}

function pickerLabel(group: ConversationGroup, grouped: boolean): string {
  return `${channelInfo(group.channel).label}, ${pickerDetail(group, grouped)}`;
}

function ChannelIcon({ channel }: { channel: string }) {
  const { icon: Icon } = channelInfo(channel);
  return <Icon className='size-3.5 shrink-0 text-ink-muted' aria-hidden='true' />;
}

/* Only a dot: a menu item is a button, and a link cannot sit inside it. */
function GroupFindingsDot({ group }: { group: ConversationGroup }) {
  const attention = attentionOf(
    group.conversations.flatMap((conversation) =>
      conversation.open_reviews.flatMap((review) =>
        review.findings.map((finding) => finding.severity),
      ),
    ),
  );
  if (!attention) return null;

  return (
    <span
      role='img'
      aria-label={`${attention.count} open ${attention.count === 1 ? 'finding' : 'findings'}`}
      className={cn(
        'size-2 shrink-0 rounded-full',
        SEVERITY_MARK_CLASS[attention.severity],
      )}
    />
  );
}
